import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import { getPreviousPartners, PartnerInfo } from '../../services/inviteService'

export interface PreviousPartnersListProps {
  hostUid: string
  diveId: string
  onSelect: (data: { handle: string; userId: string }) => void
}

const PreviousPartnersList = ({
  hostUid,
  diveId,
  onSelect
}: PreviousPartnersListProps) => {
  const [partners, setPartners] = useState<PartnerInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPartners = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await getPreviousPartners(hostUid, diveId)
      setPartners(result)
    } catch {
      setError('Failed to load previous dive partners')
    } finally {
      setIsLoading(false)
    }
  }, [hostUid, diveId])

  useEffect(() => {
    fetchPartners()
  }, [fetchPartners])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          p: 3
        }}
      >
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
        <Button variant='outlined' size='small' onClick={fetchPartners}>
          Retry
        </Button>
      </Box>
    )
  }

  if (partners.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          No previous dive partners
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {partners.map(partner => (
        <Box
          key={partner.userId}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            cursor: 'pointer'
          }}
          onClick={() =>
            onSelect({ handle: partner.handle ?? '', userId: partner.userId })
          }
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSelect({ handle: partner.handle ?? '', userId: partner.userId })
            }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant='body1'>{partner.displayName}</Typography>
            {partner.handle && (
              <Typography variant='body2' color='text.secondary'>
                @{partner.handle}
              </Typography>
            )}
          </Box>
          <Chip label='Select' size='small' color='primary' clickable />
        </Box>
      ))}
    </Box>
  )
}

export default PreviousPartnersList
